import request from '@/utils/request'
import { CUT_TYPE, POLICY_BASE_PATH, SHIPPER_CODE } from './consts'
import { AllocationPriorityApiError, POLICY_ERROR_MESSAGE, toPolicyError, unwrapEnvelope } from './errors'
import type {
  CapacityResponse,
  CreateOperationRequest,
  DeletePolicyRequest,
  OutboundPolicyResponse,
  PolicyItems,
  ReplaceOperationRequest,
} from './types'

/** CAPA 조회 고정 축 (LLD REQ-17). shipperCode 는 권한 검사용이다. */
const CAPACITY_BUSINESS_TYPE = 'B2C'
const CAPACITY_TARGET_TYPE = 'WAREHOUSE'

/** 한 번만 호출한다. 실패(409 포함)를 재전송하지 않고 Error 로 바꿔 던진다. */
const call = async <T>(send: () => Promise<unknown>): Promise<T | undefined> => {
  let body: unknown
  try {
    body = await send()
  } catch (error_) {
    throw toPolicyError(error_)
  }
  return unwrapEnvelope<T>(body)
}

const requirePolicy = (data: OutboundPolicyResponse | undefined): OutboundPolicyResponse => {
  if (!data || typeof data.id !== 'number') throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  return data
}

export const fetchPolicies = async (): Promise<OutboundPolicyResponse[]> => {
  const data = await call<PolicyItems<OutboundPolicyResponse>>(() =>
    request.get(POLICY_BASE_PATH, { shipperCode: SHIPPER_CODE, cutType: CUT_TYPE }),
  )
  if (!data || !Array.isArray(data.items)) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  return data.items
}

export const createOperation = async (body: CreateOperationRequest): Promise<OutboundPolicyResponse> =>
  requirePolicy(await call<OutboundPolicyResponse>(() => request.post(`${POLICY_BASE_PATH}/operations`, body)))

/** 교체 응답 data 는 쓰지 않는다(쓰기 후 재조회). envelope 만 검증한다 (REQ-10). */
export const replaceOperation = async (id: number, body: ReplaceOperationRequest): Promise<void> => {
  await call<OutboundPolicyResponse>(() => request.put(`${POLICY_BASE_PATH}/${id}`, body))
}

export const deletePolicy = async (id: number, body: DeletePolicyRequest): Promise<void> => {
  await call<never>(() => request.deleteWithBody(`${POLICY_BASE_PATH}/${id}`, body))
}

const isCapacityWarehouse = (w: unknown): boolean => {
  if (typeof w !== 'object' || w === null) return false
  const { warehouseCode, mode, target, used, available } = w as Record<string, unknown>
  if (typeof warehouseCode !== 'string' || !warehouseCode.trim()) return false
  if (mode !== 'LIMITED' && mode !== 'UNLIMITED') return false
  return [target, used, available].every(isOptionalCount)
}
/** 없음/null 허용, 그 외에는 0 이상 정수. */
const isOptionalCount = (v: unknown): boolean => v === undefined || v === null || (Number.isInteger(v) && (v as number) >= 0)

/** 운영일(YYYY-MM-DD) CAPA. 한 번만 호출하고, 형식이 맞지 않으면 일반 실패다 (Mock fallback 없음). */
export const fetchCapacity = async (operationDate: string): Promise<CapacityResponse> => {
  const data = await call<CapacityResponse>(() =>
    request.get(`${POLICY_BASE_PATH}/capacity`, {
      shipperCode: SHIPPER_CODE,
      businessType: CAPACITY_BUSINESS_TYPE,
      operationTargetType: CAPACITY_TARGET_TYPE,
      operationDate,
    }),
  )
  if (!data || (data.status !== 'AVAILABLE' && data.status !== 'UNAVAILABLE')) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  // UNAVAILABLE 은 빈 배열이라 생략될 수 있다. AVAILABLE 은 배열 필수.
  const warehouses = data.warehouses ?? (data.status === 'UNAVAILABLE' ? [] : undefined)
  if (!Array.isArray(warehouses) || !warehouses.every(isCapacityWarehouse)) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  if (new Set(warehouses.map((w) => w.warehouseCode)).size !== warehouses.length) throw new AllocationPriorityApiError(POLICY_ERROR_MESSAGE.GENERIC)
  return { ...data, warehouses }
}
